import MontuMarkdown from "@repo/components/src/shared/markdown";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import dayjs from "dayjs";
import { Send } from "lucide-react-native";
import React from "react";
import type { ViewStyle } from "react-native";
import DateChip from "./DateChip";
import type { PostTypeInfo } from "./postTypeLookup";
import type { DraftPreviewContent } from "../draftPreviewTypes";

type Props = {
  content: DraftPreviewContent;
  postType: PostTypeInfo | undefined;
  index: number;
};

/** `animationDelay` is web-runtime-only — same carve-out `ThumbnailDeck`'s own
 * delay helper takes. Staggers the deliverables popping in once the section
 * flips from "writing" to real content, instead of all rows landing at once. */
const delayStyle = (index: number): ViewStyle =>
  ({ animationDelay: `${index * 70}ms` }) as ViewStyle;

/**
 * One deliverable, styled like `apps/mobil/.../deliverables/DeliverableItem.tsx`.
 * The draft's `content` entries carry less than a real `ContentRequirement` — no
 * mentions, hashtags or uploader yet, those only exist once a real campaign is
 * created from the draft — so this ports the index badge, the platform/post-type
 * title, a single delivery date chip and the description inset, and leaves the
 * rest out rather than inventing it.
 */
const DeliverableCard = React.memo(({ content, postType, index }: Props) => {
  const title = postType
    ? `${postType.platformName} · ${postType.postTypeName}`
    : (content.label ?? `Contenido ${index}`);
  const deliveryLabel = content.date
    ? dayjs(content.date).format("DD MMM")
    : null;

  return (
    <Box
      style={delayStyle(index)}
      className="rounded-3xl bg-white overflow-hidden border border-outline-100 animate-fade-rise motion-reduce:animate-none"
    >
      <VStack className="px-4 pt-4 pb-3" space="sm">
        <HStack space="md" className="items-center">
          <Box className="rounded-xl items-center justify-center w-8 h-8 bg-brand-50">
            <Text bold size="sm" className="text-brand-400">
              {index}
            </Text>
          </Box>
          <Text
            bold
            size="md"
            className="text-typography-950 flex-1 tracking-tight"
            numberOfLines={2}
          >
            {title}
          </Text>
        </HStack>

        {deliveryLabel ? (
          <HStack space="md" className="flex-wrap">
            <DateChip icon={Send} label="Entrega" value={deliveryLabel} />
          </HStack>
        ) : null}

        {content.script?.trim() ? (
          <Box className="rounded-2xl bg-background-50 border border-outline-50 p-3">
            <MontuMarkdown baseSize={13}>{content.script}</MontuMarkdown>
          </Box>
        ) : null}
      </VStack>
    </Box>
  );
});

DeliverableCard.displayName = "DeliverableCard";

export default DeliverableCard;
